import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DollarSign, Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

// Use window.location.origin for API calls to work on any domain
const API_URL = window.location.origin;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setError('No reset token provided');
        return;
      }

      try {
        const response = await axios.post(`${API_URL}/api/auth/validate-reset-token`, { token });
        if (response.data.valid) {
          setTokenValid(true);
          setTokenEmail(response.data.email || '');
        } else {
          setError(response.data.message || 'Invalid or expired reset link');
        }
      } catch (err) {
        setError('Invalid or expired reset link');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setResetSuccess(true);
      toast.success('Password updated successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex flex-col">
        <header className="p-4 sm:p-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/logo-64.png" alt="Payday Control" className="w-10 h-10 rounded-xl" />
            <span className="font-heading font-bold text-lg text-white">Payday Control</span>
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-[#63E6E2] animate-spin mx-auto mb-4" />
            <p className="text-[#9CA3AF]">Validating reset link...</p>
          </div>
        </main>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !resetSuccess) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex flex-col">
        <header className="p-4 sm:p-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/logo-64.png" alt="Payday Control" className="w-10 h-10 rounded-xl" />
            <span className="font-heading font-bold text-lg text-white">Payday Control</span>
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-sm text-center"
          >
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Invalid Reset Link
            </h1>
            <p className="text-[#9CA3AF] mb-6">
              {error || 'This password reset link is invalid or has expired.'}
            </p>
            <Link to="/forgot-password">
              <Button className="w-full h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold">
                Request new reset link
              </Button>
            </Link>
            <div className="mt-4">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-[#9CA3AF] hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex flex-col">
        <header className="p-4 sm:p-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/logo-64.png" alt="Payday Control" className="w-10 h-10 rounded-xl" />
            <span className="font-heading font-bold text-lg text-white">Payday Control</span>
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm text-center"
          >
            <div className="w-16 h-16 bg-[#63E6E2]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#63E6E2]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Password Updated!
            </h1>
            <p className="text-[#9CA3AF] mb-6">
              Your password has been successfully updated. Please log in with your new password.
            </p>
            <Link to="/login">
              <Button 
                className="w-full h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
                data-testid="go-to-login-btn"
              >
                Go to sign in
              </Button>
            </Link>
          </motion.div>
        </main>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-[#0F1224] flex flex-col">
      <header className="p-4 sm:p-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/logo-64.png" alt="Payday Control" className="w-10 h-10 rounded-xl" />
          <span className="font-heading font-bold text-lg text-white">Payday Control</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
              Reset your password
            </h1>
            <p className="text-[#9CA3AF]">
              Create a new password for{' '}
              <span className="text-white">{tokenEmail}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-[#9CA3AF]">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-10 pr-10 h-12 bg-[#1B1F3A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2] focus:ring-[#63E6E2]/20"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#9CA3AF]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-[#6B7280]">Must be at least 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#9CA3AF]">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="pl-10 pr-10 h-12 bg-[#1B1F3A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2] focus:ring-[#63E6E2]/20"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#9CA3AF]"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-400">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || newPassword !== confirmPassword || newPassword.length < 6}
              className="w-full h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold btn-press disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                'Reset password'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-[#9CA3AF] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
